import { CollapseSection } from "@msys/ui";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import * as Yup from "yup";
import { AskWhen, AskWhom } from "../../../clients/graphqlTypes.js";
import { SelectMultipleField } from "../../commons/form-fields/SelectMultipleField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { LabeledQuestionControlSection } from "./LabeledQuestionControlSection.js";
import { useQuestionContol } from "./useQuestionContol.js";

export interface QuestionControlSectionFormValues {
  questionControl: Array<{
    askWhom: AskWhom[];
    askWhen: AskWhen[];
  }>;
  prompt: string;
}

interface Props {
  isInitiallyExpanded?: boolean;
  promptPlaceholder?: string;
  allowedAskWhen?: AskWhen[];
  readOnly?: boolean;
  disabled?: boolean;
  hidePrompt?: boolean;
}

export const QuestionControlSection = ({
  isInitiallyExpanded = false,
  promptPlaceholder,
  allowedAskWhen,
  readOnly,
  disabled,
  hidePrompt,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const formikProps = useFormikContext<QuestionControlSectionFormValues>();

  return (
    <CollapseSection
      title={t("Question control", {
        ns: "QuoteItem",
      })}
      isInitiallyExpanded={isInitiallyExpanded}
    >
      {!readOnly ? (
        <QuestionControlForm
          promptPlaceholder={promptPlaceholder}
          allowedAskWhen={allowedAskWhen}
          disabled={disabled}
          hidePrompt={hidePrompt}
        />
      ) : (
        <LabeledQuestionControlSection
          allowedAskWhen={allowedAskWhen}
          questionControl={formikProps.values.questionControl}
          prompt={formikProps.values.prompt}
          hidePrompt={hidePrompt}
        />
      )}
    </CollapseSection>
  );
};

export const QuestionControlForm = ({
  promptPlaceholder,
  allowedAskWhen,
  disabled,
  hidePrompt,
}: Omit<Props, "isInitiallyExpanded" | "readOnly">) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const { askWhomOptions, askWhenOptions } = useQuestionContol(allowedAskWhen);
  const formikProps = useFormikContext<QuestionControlSectionFormValues>();
  return (
    <Stack direction="column" spacing={1}>
      {formikProps.values.questionControl.map((qc, idx, arr) => {
        return (
          <div key={idx}>
            <Stack direction={"row"} spacing={1}>
              <SelectMultipleField
                name={`questionControl[${idx}].askWhen`}
                label={t("Ask when", { ns: "QuoteItem" })}
                options={askWhenOptions}
                disabled={disabled || formikProps.isSubmitting}
              />
              <SelectMultipleField
                name={`questionControl[${idx}].askWhom`}
                options={askWhomOptions}
                label={t("Ask whom", { ns: "QuoteItem" })}
                disabled={disabled || formikProps.isSubmitting}
              />
              <IconButton
                color="primary"
                sx={{ alignSelf: "flex-start" }}
                onClick={async () => {
                  const copy = [...formikProps.values.questionControl];
                  copy.splice(idx, 1);

                  await formikProps.setFieldValue("questionControl", copy);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
            {idx < arr.length - 1 ? (
              <Typography color="grey.600" variant="caption">
                {t("or", { ns: "Global" })}
              </Typography>
            ) : null}
          </div>
        );
      })}
      <Button
        variant="text"
        color="secondary"
        startIcon={<AddIcon />}
        sx={{ alignSelf: "flex-start" }}
        onClick={async () => {
          await formikProps.setFieldValue("questionControl", [
            ...formikProps.values.questionControl,
            { askWhen: [], askWhom: [] },
          ]);
        }}
        disabled={disabled || formikProps.isSubmitting}
      >
        {t("Add", { ns: "Global" })}
      </Button>
      {!hidePrompt && (
        <TextField
          name={"prompt"}
          label={t("Prompt", { ns: "QuoteItem" })}
          placeholder={promptPlaceholder}
          disabled={disabled || formikProps.isSubmitting}
        />
      )}
    </Stack>
  );
};

export const defaultQuestionControlValues: QuestionControlSectionFormValues = {
  questionControl: [
    {
      askWhen: [],
      askWhom: ["contractor"],
    },
  ],
  prompt: "",
};

export const useQuestionControlValidationSchema = () => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  return React.useMemo(
    () => ({
      questionControl: Yup.array().of(
        Yup.object().shape({
          askWhen: Yup.array(Yup.string()),
          askWhom: Yup.array(Yup.string()),
        })
      ),
      prompt: Yup.string().label(t("Prompt", { ns: "QuoteItem" })),
    }),
    [t]
  );
};
